import React from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  Verified as VerifiedIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { CommonButton } from "../../../../Components/ui";
import {
  getSymbolsByCategory,
  JACKPOT_TIER_THEMES,
  SLOT_MACHINE_FRAME_THEME,
} from "./fiveSlotConfig";
import fiveSlotBg from "../../../../../static/Images/newlyAdded/5_slot_game.png";
import { borderRadiusPx } from "../../../../../theme";
import FiveSlotSymbolIcon from "./FiveSlotSymbolIcon";

const FiveSlotMachineCard = ({ lotteryData, onPlay }) => {
  const history = useHistory();

  const category =
    lotteryData?.slotCategory ||
    (lotteryData?.name?.toLowerCase().includes("crypto") ? "crypto" : "fiat");
  const currency = lotteryData?.currency || "BET";
  const activeSymbols = getSymbolsByCategory(category);

  // Preview all 5 symbols in order: [0, 1, 2, 3, 4]
  const previewSymbols = [
    activeSymbols[0],
    activeSymbols[1],
    activeSymbols[2],
    activeSymbols[3],
    activeSymbols[4],
  ];

  const handleOpenLottery = () => {
    if (onPlay && typeof onPlay === "function") {
      onPlay(lotteryData.lotteryId);
    } else {
      history.push(`/five-slotlottery/${lotteryData.lotteryId}`);
    }
  };

  const handleShare = (event) => {
    event.stopPropagation();
    const url = `${window.location.origin}/five-slotlottery/${lotteryData.lotteryId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success("Slot machine link copied!");
    }
  };

  const formatAmount = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) {
      return "0.00";
    }
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      className="slot-machine-card five-slot-card"
      sx={{
        cursor: "pointer",
        transition:
          "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease, border-color 0.25s ease",
        borderRadius: borderRadiusPx.xl,
        bgcolor: SLOT_MACHINE_FRAME_THEME.bg || "#ffffff",
        border: SLOT_MACHINE_FRAME_THEME.border || "1px solid #e2e8f0",
        boxShadow:
          SLOT_MACHINE_FRAME_THEME.boxShadow ||
          "0 4px 20px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(37, 99, 235, 0.12)",
          borderColor: "#2563eb",
        },
        p: { xs: 2, sm: 2.5 },
      }}
      onClick={handleOpenLottery}
    >
      {/* Platform Consistent Top Accent Gradient Bar (5-Slot Signature Blue, Purple & Gold) */}
      <Box
        sx={{
          height: 4,
          background:
            "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 40%, #eab308 100%)",
          mx: { xs: -2, sm: -2.5 },
          mt: { xs: -2, sm: -2.5 },
          mb: 2,
        }}
      />

      {/* Card Header */}
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: borderRadiusPx?.md || "8px",
                bgcolor: "#eff6ff",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #dbeafe",
                fontSize: "1.2rem",
                boxShadow: "0 2px 6px rgba(37, 99, 235, 0.12)",
              }}
            >
              🎰
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    fontSize: "0.95rem",
                  }}
                >
                  {lotteryData.name || `5-SlotBET #${lotteryData.lotteryId}`}
                </Typography>
                <VerifiedIcon
                  sx={{
                    fontSize: 15,
                    color: "#2563eb",
                  }}
                />
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: "#2563eb",
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                {category === "crypto"
                  ? "Crypto Slot • 5-Reel"
                  : "Classic Vegas • 5-Reel"}
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Share Slot Machine" arrow>
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                color: "#64748b",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                p: 0.6,
                "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" },
              }}
            >
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Jackpots Strip (Mega, Major, Mini) matching detail page styling */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          mb: 2,
        }}
      >
        {/* MEGA */}
        <Box
          sx={{
            py: 0.85,
            px: 0.5,
            textAlign: "center",
            borderRadius: "8px",
            background: JACKPOT_TIER_THEMES.mega.background,
            border: JACKPOT_TIER_THEMES.mega.border,
            boxShadow: JACKPOT_TIER_THEMES.mega.boxShadow,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: JACKPOT_TIER_THEMES.mega.titleColor,
              fontWeight: 800,
              fontSize: "0.6rem",
              display: "block",
              letterSpacing: "0.5px",
            }}
          >
            MEGA
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 900,
              color: JACKPOT_TIER_THEMES.mega.valueColor,
              fontSize: "0.78rem",
              lineHeight: 1.2,
              fontFamily: "monospace",
            }}
          >
            {formatAmount(lotteryData?.jackpots?.mega?.amount || 128900)}
          </Typography>
        </Box>

        {/* MAJOR */}
        <Box
          sx={{
            py: 0.85,
            px: 0.5,
            textAlign: "center",
            borderRadius: "8px",
            background: JACKPOT_TIER_THEMES.major.background,
            border: JACKPOT_TIER_THEMES.major.border,
            boxShadow: JACKPOT_TIER_THEMES.major.boxShadow,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: JACKPOT_TIER_THEMES.major.titleColor,
              fontWeight: 800,
              fontSize: "0.6rem",
              display: "block",
              letterSpacing: "0.5px",
            }}
          >
            MAJOR
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 900,
              color: JACKPOT_TIER_THEMES.major.valueColor,
              fontSize: "0.78rem",
              lineHeight: 1.2,
              fontFamily: "monospace",
            }}
          >
            {formatAmount(lotteryData?.jackpots?.major?.amount || 16450)}
          </Typography>
        </Box>

        {/* MINI */}
        <Box
          sx={{
            py: 0.85,
            px: 0.5,
            textAlign: "center",
            borderRadius: "8px",
            background: JACKPOT_TIER_THEMES.mini.background,
            border: JACKPOT_TIER_THEMES.mini.border,
            boxShadow: JACKPOT_TIER_THEMES.mini.boxShadow,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: JACKPOT_TIER_THEMES.mini.titleColor,
              fontWeight: 800,
              fontSize: "0.6rem",
              display: "block",
              letterSpacing: "0.5px",
            }}
          >
            MINI
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 900,
              color: JACKPOT_TIER_THEMES.mini.valueColor,
              fontSize: "0.78rem",
              lineHeight: 1.2,
              fontFamily: "monospace",
            }}
          >
            {formatAmount(lotteryData?.jackpots?.mini?.amount || 2840.5)}
          </Typography>
        </Box>
      </Box>

      {/* 5-Reels Mini Preview Stage */}
      <Box
        sx={{
          borderRadius: "14px",
          position: "relative",
          overflow: "hidden",
          p: 1.25,
          mb: 2,
          border: "1.5px solid #e2e8f0",
          backgroundImage: `url(${fiveSlotBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Light Glass Overlay for clean contrast */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255, 255, 255, 0.82)",
            backdropFilter: "blur(2px)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 0.5,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          {previewSymbols.map((sym, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #e2e8f0 100%)",
                py: 0.75,
                px: 0.25,
                borderRadius: "8px",
                border: "1.5px solid rgba(203, 213, 225, 0.8)",
                boxShadow:
                  "inset 0 2px 4px rgba(255, 255, 255, 0.9), inset 0 -2px 4px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.06)",
              }}
            >
              <FiveSlotSymbolIcon symbol={sym} size="sm" />
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  color: "#334155",
                  mt: 0.35,
                  textAlign: "center",
                  lineHeight: 1.1,
                }}
              >
                {sym.tier || sym.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bet Range / Ticket Price Specs Info */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 2,
          px: 1.25,
          py: 0.75,
          bgcolor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.72rem" }}
        >
          {lotteryData?.ticketPrice ? "Ticket Price: " : "Bet Range: "}
          <Box
            component="span"
            sx={{ color: "#0f172a", fontWeight: 800, fontFamily: "monospace" }}
          >
            {lotteryData?.ticketPrice
              ? `${lotteryData.ticketPrice} ${currency}`
              : `${lotteryData?.minBet || "1.0"} - ${lotteryData?.maxBet || "500.0"} ${currency}`}
          </Box>
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: "#2563eb",
            fontWeight: 800,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          5 Reels • 1 Payline
        </Typography>
      </Stack>

      {/* Play Action Button */}
      <CommonButton
        variant="primary"
        size="small"
        fullWidth
        startIcon={<PlayIcon />}
        onClick={handleOpenLottery}
        sx={{
          py: 1.1,
          borderRadius: "10px",
          fontWeight: 800,
          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          color: "#ffffff",
          letterSpacing: "0.8px",
          fontSize: "0.88rem",
          boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
          "&:hover": {
            background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
            boxShadow: "0 6px 18px rgba(37, 99, 235, 0.45)",
          },
        }}
      >
        PLAY 5-SLOT
      </CommonButton>
    </Box>
  );
};

export default FiveSlotMachineCard;